// Company stores (S4 shop, 2026-10-12) — /store/cart: the cart and checkout.
//
//   Items      editable sizes per colour, remove a colour or an item; each
//              line's price is the server's (POST /portal/store/cart/price,
//              re-asked as anything changes, debounced).
//   Ship to    name, company, street, city, state, ZIP — US only, the
//              website cart's rules (5-digit ZIP that matches the state;
//              business or home).
//   Pay        only the store's payment options, in plain words.
//   Approval   said up front when the store's rule will send it to the admin.
//   Submit     POST /portal/store/orders with the cart's idempotency key —
//              a double click or a retry is the same order. Then: waiting for
//              approval → a confirmation; a card to pay → Stripe Checkout;
//              invoice → placed.
import { cloneElement, useEffect, useId, useMemo, useRef, useState, type ReactElement } from "react";
import { Link } from "react-router-dom";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PortalLayout } from "@/components/PortalLayout";
import { StoreGate, StoreHeading } from "@/components/StoreGate";
import { CARD, H2, INPUT, LINK, MoneyRow, Notice, PRIMARY, SECONDARY, StatusPill, useMyCart } from "@/components/StoreShopUi";
import { apiMessage, storeAddressLine, storeOffMessage, type MyStore, type PaymentOption } from "@/lib/portal-store";
import type { StoreCart } from "@/lib/store-cart";
import {
  approvalNeeded,
  cleanItems,
  completeShipTo,
  EMPTY_SHIP_TO,
  isCartChanged,
  money,
  paymentChoiceText,
  pieces,
  priceStoreCart,
  shipToProblems,
  STORE_ORDERS_KEY,
  STORE_PENDING_KEY,
  submitStoreOrder,
  US_STATES,
  zipState,
  type ShipTo,
  type StoreOrder,
  type SubmitResult,
} from "@/lib/store-shop";

type Done = { kind: "approval" | "placed" | "received" | "pay"; order: StoreOrder };

export function StoreCartPage() {
  const [done, setDone] = useState<Done | null>(null);
  return (
    <PortalLayout>
      <StoreGate>
        {(my) => (
          <>
            <StoreHeading store={my.store} title={done ? "Thank you" : "Your cart"} />
            {done ? <OrderDone done={done} /> : <CartView my={my} onDone={setDone} />}
          </>
        )}
      </StoreGate>
    </PortalLayout>
  );
}

function OrderDone({ done }: { done: Done }) {
  const o = done.order;
  return (
    <section className={`${CARD} space-y-4`}>
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-xl font-bold tracking-tight">Order {o.reference}</h2>
        <StatusPill order={o} />
      </div>
      {done.kind === "approval" && (
        <p className="text-sm text-ink">
          Sent to your store admin for approval. We'll email you as soon as they decide
          {o.paymentMethod === "employee_card" ? " — and if it's approved, a link to pay." : "."}
        </p>
      )}
      {done.kind === "placed" && <p className="text-sm text-ink">Your order is placed and on its way into production. We've emailed you a copy.</p>}
      {done.kind === "received" && (
        <p className="text-sm text-ink">We have your order. Our team is putting it into production and will be in touch if anything needs checking.</p>
      )}
      {done.kind === "pay" && <p className="text-sm text-ink">Your order is saved. Pay for it from Your orders whenever you're ready.</p>}
      {o.total != null && <p className="text-sm text-brown-400">Total {money(o.total)}{o.tax == null && o.paymentMethod !== "invoice" ? " plus any sales tax" : ""}</p>}
      <div className="flex flex-wrap gap-3">
        <Link to={`/store/orders?order=${encodeURIComponent(o.id)}`} className={PRIMARY}>
          See your order
        </Link>
        <Link to="/store" className={SECONDARY}>
          Back to the store
        </Link>
      </div>
    </section>
  );
}

function CartView({ my, onDone }: { my: MyStore; onDone: (d: Done) => void }) {
  const qc = useQueryClient();
  const { cart, update, clearAfterOrder, renewKey, ready } = useMyCart();
  const products = useMemo(() => new Map(my.products.map((p) => [p.productId, p])), [my.products]);

  // A store with delivery addresses ships to one of them (Stephanie
  // 2026-10-22): the person gives their name; the address is the store's.
  const storeAddresses = my.store.shipToList?.length ? my.store.shipToList : null;
  const pickedAt = storeAddresses ? Math.max(0, storeAddresses.findIndex((a) => a.street1 === cart.shipTo?.street1 && a.postalCode === cart.shipTo?.postalCode)) : -1;
  const picked = storeAddresses ? storeAddresses[pickedAt]! : null;
  const ship: ShipTo = picked
    ? { ...EMPTY_SHIP_TO, name: cart.shipTo?.name ?? "", phone: cart.shipTo?.phone ?? "", company: picked.company ?? "", street1: picked.street1, street2: picked.street2 ?? "", city: picked.city, stateCode: picked.stateCode, postalCode: picked.postalCode, residential: picked.residential }
    : cart.shipTo ?? { ...EMPTY_SHIP_TO, company: my.store.companyName ?? "" };
  const options = my.store.paymentOptions;
  const method: PaymentOption | null =
    cart.paymentMethod && options.includes(cart.paymentMethod) ? cart.paymentMethod : options.length === 1 ? options[0]! : null;

  const inStore = cart.items.filter((i) => products.has(i.productId));
  const items = cleanItems(inStore);
  const qty = items.reduce((n, i) => n + i.colorQuantities.reduce((m, r) => m + Object.values(r.quantities).reduce((a, b) => a + b, 0), 0), 0);
  const address = completeShipTo(ship);

  // Debounced price: items + the address once it's complete.
  const sig = JSON.stringify([items, address ? { ...address, company: "" } : null]);
  const [debounced, setDebounced] = useState(sig);
  useEffect(() => {
    const t = window.setTimeout(() => setDebounced(sig), 450);
    return () => window.clearTimeout(t);
  }, [sig]);
  const price = useQuery({
    queryKey: ["portal-store-price", "cart", debounced],
    queryFn: () => {
      const [i, a] = JSON.parse(debounced) as [typeof items, ShipTo | null];
      return priceStoreCart(i, a);
    },
    enabled: items.length > 0 && debounced === sig,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
    retry: false,
  });
  const p = items.length > 0 ? price.data : undefined;
  const settling = items.length > 0 && (debounced !== sig || price.isFetching);
  const priced = p?.status === "priced" ? p : null;
  const blockedAt = p?.status === "blocked" ? p.itemIndex : null;

  const [showProblems, setShowProblems] = useState(false);
  const problems = shipToProblems(ship);
  const needs = approvalNeeded(my, method, priced?.total ?? null);
  const paused = my.store.status !== "live";

  const busy = useRef(false);
  const [redirecting, setRedirecting] = useState(false);
  const submit = useMutation({
    mutationFn: (c: StoreCart) => submitStoreOrder({ items, shipTo: address!, paymentMethod: method!, idempotencyKey: c.idempotencyKey }),
    onSuccess: (r: SubmitResult) => {
      void qc.invalidateQueries({ queryKey: STORE_ORDERS_KEY });
      void qc.invalidateQueries({ queryKey: STORE_PENDING_KEY });
      clearAfterOrder();
      if (r.payment?.url) {
        setRedirecting(true);
        window.location.assign(r.payment.url);
        return;
      }
      const s = r.order.status;
      busy.current = false;
      if (s === "pending_approval") onDone({ kind: "approval", order: r.order });
      else if (r.placed || s === "placed") onDone({ kind: "placed", order: r.order });
      else if (s === "approved" || s === "paid") onDone({ kind: "received", order: r.order });
      else onDone({ kind: "pay", order: r.order });
    },
    onError: (err) => {
      busy.current = false;
      // The server says this key belongs to a different cart: a new key, and
      // the person submits again with their next click.
      if (isCartChanged(err)) renewKey();
    },
  });

  function setShip(patch: Partial<ShipTo>) {
    update((c) => {
      const cur = c.shipTo ?? ship;
      const next = { ...cur, ...patch };
      // A full ZIP fills in an empty state (the website's ZIP table).
      if (patch.postalCode !== undefined && !next.stateCode) {
        const st = zipState(next.postalCode);
        if (/^\d{5}/.test(next.postalCode.trim()) && US_STATES.some(([code]) => code === st)) next.stateCode = st;
      }
      return { ...c, shipTo: next };
    });
  }

  function setQty(productId: string, color: string, size: string, value: string) {
    const n = Math.max(0, Math.min(99_999, Math.floor(Number(value) || 0)));
    update((c) => ({
      ...c,
      items: c.items.map((i) =>
        i.productId !== productId ? i : { ...i, colorQuantities: i.colorQuantities.map((r) => (r.color !== color ? r : { ...r, quantities: { ...r.quantities, [size]: n } })) },
      ),
    }));
  }

  function removeColour(productId: string, color: string) {
    update((c) => ({
      ...c,
      items: c.items
        .map((i) => (i.productId !== productId ? i : { ...i, colorQuantities: i.colorQuantities.filter((r) => r.color !== color) }))
        .filter((i) => i.colorQuantities.length > 0),
    }));
  }

  function removeItem(productId: string) {
    update((c) => ({ ...c, items: c.items.filter((i) => i.productId !== productId) }));
  }

  function onSubmit() {
    if (busy.current || submit.isPending || redirecting) return;
    setShowProblems(true);
    if (!address || !method || items.length === 0 || !priced || paused) return;
    busy.current = true;
    submit.mutate(cart);
  }

  if (!ready) return null;

  if (cart.items.length === 0) {
    return (
      <div className={CARD}>
        <p className="text-sm text-brown-400">
          Your cart is empty. <Link to="/store" className={LINK}>Browse the store</Link>
        </p>
      </div>
    );
  }

  // The position of each in-store item among the priced items (blocked messages name an index).
  const pricedIndex = new Map(items.map((i, at) => [i.productId, at]));
  const locked = submit.isPending || redirecting;
  const action =
    needs === "yes" ? "Send for approval" : method === "invoice" ? "Place order" : method ? "Continue to payment" : "Place order";

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_23.75rem]">
      <div className="min-w-0 space-y-6">
        {/* Items */}
        <section className={CARD} aria-labelledby="cart-items">
          <h2 id="cart-items" className={H2}>
            Items
          </h2>
          <ul className="mt-3 divide-y divide-rule">
            {cart.items.map((it) => {
              const prod = products.get(it.productId);
              const at = pricedIndex.get(it.productId);
              const line = at !== undefined ? priced?.items[at] : undefined;
              return (
                <li key={it.productId} className="py-4">
                  <div className="flex gap-3">
                    <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md border border-rule bg-canvas">
                      {prod?.photo && <img src={prod.photo} alt="" className="h-full w-full object-contain p-1" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-ink">{prod?.name ?? "No longer in the store"}</p>
                          {prod?.branding && <p className="text-xs text-brown-400">Branded: {prod.branding.charAt(0).toLowerCase() + prod.branding.slice(1)}</p>}
                        </div>
                        {line && (
                          <p className={`shrink-0 text-right text-sm tabular-nums ${settling ? "opacity-60" : ""}`}>
                            <span className="block font-semibold text-ink">{money(line.total)}</span>
                            <span className="text-xs text-brown-400">{money(line.each)} each</span>
                          </p>
                        )}
                      </div>
                      {!prod && <p className="mt-1 text-xs text-rust-500">This item was taken out of your store. Remove it to check out.</p>}
                      {blockedAt !== null && at === blockedAt && p?.status === "blocked" && (
                        <p className="mt-1 text-xs text-rust-500">{p.message}</p>
                      )}
                      {line?.setup ? <p className="mt-1 text-xs text-brown-400">Includes {money(line.setup)} setup, once per order.</p> : null}
                      {line?.notice && <p className="mt-1 text-xs text-brown-400">{line.notice}</p>}
                    </div>
                  </div>
                  {prod &&
                    it.colorQuantities.map((r) => (
                      <div key={r.color} className="mt-3 rounded-md border border-rule p-3">
                        <div className="mb-2 flex items-center justify-between gap-3">
                          <span className="truncate text-sm font-medium text-ink">{r.color}</span>
                          <button type="button" disabled={locked} onClick={() => removeColour(it.productId, r.color)} className="shrink-0 text-xs text-brown-400 underline-offset-2 hover:underline">
                            Remove color
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
                          {Object.keys(r.quantities).map((z) => (
                            <label key={z} className="flex min-w-0 flex-col gap-1 text-xs text-brown-400">
                              {z}
                              <input
                                type="number"
                                inputMode="numeric"
                                min={0}
                                max={99999}
                                disabled={locked}
                                value={r.quantities[z] ? String(r.quantities[z]) : ""}
                                placeholder="0"
                                aria-label={`${prod.name} ${r.color} ${z === "Quantity" ? "quantity" : `size ${z}`}`}
                                onChange={(e) => setQty(it.productId, r.color, z, e.target.value)}
                                className="w-full min-w-0 rounded-md border border-cream-300 bg-white px-2 py-1.5 text-center text-sm text-brown-500 focus:border-peach-400 focus:outline-none"
                              />
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  <div className="mt-3 flex flex-wrap gap-4 text-xs">
                    {prod && (
                      <Link to={`/store/product/${encodeURIComponent(it.productId)}`} className={LINK}>
                        Add sizes or colors
                      </Link>
                    )}
                    <button type="button" disabled={locked} onClick={() => removeItem(it.productId)} className="text-brown-400 underline-offset-2 hover:underline">
                      Remove item
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        </section>

        {/* Ship to */}
        <section className={CARD} aria-labelledby="cart-ship">
          <h2 id="cart-ship" className={H2}>
            Ship to
          </h2>
          {storeAddresses ? (
            <fieldset disabled={locked} className="space-y-3">
              <Field label="Name (for the label)" error={showProblems ? problems.name : undefined}>
                <input className={INPUT} value={ship.name} autoComplete="name" onChange={(e) => setShip({ ...ship, name: e.target.value })} />
              </Field>
              {storeAddresses.length === 1 ? (
                <div className="rounded-md border border-rule bg-cream-50 px-3 py-2 text-sm text-ink">
                  <p className="text-xs text-brown-400">All orders from this store ship to</p>
                  {picked!.company ? <p className="font-medium">{picked!.company}</p> : null}
                  <p>{storeAddressLine(picked!)}</p>
                </div>
              ) : (
                <div className="space-y-2" role="radiogroup" aria-label="Ship to">
                  <p className="text-xs text-brown-400">Pick where your order ships</p>
                  {storeAddresses.map((a, i) => (
                    <label key={i} className="flex items-start gap-2 text-sm text-ink">
                      <input type="radio" name="store-address" className="mt-1 accent-ink" checked={i === pickedAt}
                        onChange={() => setShip({ ...ship, company: a.company ?? "", street1: a.street1, street2: a.street2 ?? "", city: a.city, stateCode: a.stateCode, postalCode: a.postalCode, residential: a.residential })} />
                      <span>
                        <span className="font-medium">{a.label || a.company || `Address ${i + 1}`}</span>
                        <span className="block text-xs text-brown-400">{storeAddressLine(a)}</span>
                      </span>
                    </label>
                  ))}
                </div>
              )}
              <p className="text-xs text-brown-400">Need it shipped somewhere else? Ask your store admin or your rep at other.</p>
            </fieldset>
          ) : (
          <>
          <p className="mb-4 text-xs text-brown-400">US addresses only for now. Ask your store admin if you need it shipped elsewhere.</p>
          <fieldset disabled={locked} className="grid gap-3 sm:grid-cols-2">
            <Field label="Name" error={showProblems ? problems.name : undefined}>
              <input className={INPUT} value={ship.name} autoComplete="shipping name" onChange={(e) => setShip({ name: e.target.value })} />
            </Field>
            <Field label="Company (optional)">
              <input className={INPUT} value={ship.company ?? ""} autoComplete="shipping organization" onChange={(e) => setShip({ company: e.target.value })} />
            </Field>
            <Field label="Street address" error={showProblems ? problems.street1 : undefined} wide>
              <input className={INPUT} value={ship.street1} autoComplete="shipping address-line1" onChange={(e) => setShip({ street1: e.target.value })} />
            </Field>
            <Field label="Apartment, suite, floor (optional)" wide>
              <input className={INPUT} value={ship.street2 ?? ""} autoComplete="shipping address-line2" onChange={(e) => setShip({ street2: e.target.value })} />
            </Field>
            <Field label="City" error={showProblems ? problems.city : undefined}>
              <input className={INPUT} value={ship.city} autoComplete="shipping address-level2" onChange={(e) => setShip({ city: e.target.value })} />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="State" error={showProblems ? problems.stateCode : undefined}>
                <select className={INPUT} value={ship.stateCode} autoComplete="shipping address-level1" onChange={(e) => setShip({ stateCode: e.target.value })}>
                  <option value="">Choose</option>
                  {US_STATES.map(([code, name]) => (
                    <option key={code} value={code}>
                      {code} — {name}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="ZIP code" error={showProblems || /^\d{5}/.test(ship.postalCode) ? problems.postalCode : undefined}>
                <input
                  className={INPUT}
                  value={ship.postalCode}
                  inputMode="numeric"
                  maxLength={10}
                  autoComplete="shipping postal-code"
                  onChange={(e) => setShip({ postalCode: e.target.value })}
                />
              </Field>
            </div>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm sm:col-span-2">
              <span className="text-brown-400">Delivering to</span>
              <label className="flex items-center gap-2">
                <input type="radio" name="residential" checked={!ship.residential} onChange={() => setShip({ residential: false })} className="accent-ink" />
                A business
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="residential" checked={ship.residential} onChange={() => setShip({ residential: true })} className="accent-ink" />
                A home
              </label>
            </div>
          </fieldset>
          </>
          )}
        </section>
      </div>

      <div className="min-w-0 space-y-6">
        {/* A store that ships to its own address is billed one way the rep
            set (Stephanie 2026-10-22) — nothing to choose here. */}
        {storeAddresses && options.length === 1 ? (
          <section className={CARD} aria-labelledby="cart-pay">
            <h2 id="cart-pay" className={H2}>Billing</h2>
            <p className="mt-2 text-sm text-ink">
              {options[0] === "invoice"
                ? `Billed to ${my.store.companyName ?? "your company"} — nothing to pay now.`
                : "Your store admin pays for this order."}
            </p>
          </section>
        ) : (
        <section className={CARD} aria-labelledby="cart-pay">
          <h2 id="cart-pay" className={H2}>
            How to pay
          </h2>
          {options.length === 0 ? (
            <p className="text-sm text-rust-500">This store isn't taking orders yet. Please ask your store admin.</p>
          ) : (
            <fieldset disabled={locked} className="mt-3 space-y-2">
              <legend className="sr-only">How to pay</legend>
              {options.map((o) => {
                const t = paymentChoiceText(o, my);
                return (
                  <label key={o} className={`flex cursor-pointer gap-3 rounded-md border px-3 py-2.5 text-sm ${method === o ? "border-ink" : "border-rule"} hover:bg-canvas`}>
                    <input type="radio" name="pay" checked={method === o} onChange={() => update((c) => ({ ...c, paymentMethod: o }))} className="mt-1 accent-ink" />
                    <span className="min-w-0">
                      <span className="block font-medium text-ink">{t.label}</span>
                      <span className="block text-xs text-brown-400">{t.help}</span>
                    </span>
                  </label>
                );
              })}
            </fieldset>
          )}
          {showProblems && !method && options.length > 0 && <p className="mt-2 text-xs text-rust-500">Choose how to pay.</p>}
        </section>
        )}

        {/* Summary */}
        <section className={CARD} aria-labelledby="cart-total" aria-live="polite">
          <h2 id="cart-total" className={H2}>
            Your total
          </h2>
          <div className={`mt-3 ${settling ? "opacity-60" : ""}`}>
            {items.length === 0 ? (
              <p className="text-sm text-brown-400">Add quantities to see your total.</p>
            ) : !p ? (
              price.isError ? (
                <p className="text-sm text-rust-500">{apiMessage(price.error, "We couldn't price your cart right now. Please try again.")}</p>
              ) : (
                <p className="text-sm text-brown-400">Working out your total…</p>
              )
            ) : p.status === "blocked" ? (
              <p className="text-sm text-rust-500">{p.message}</p>
            ) : (
              <dl className="space-y-1">
                <MoneyRow label={`Items (${pieces(p.items.reduce((n, i) => n + i.quantity, 0))})`} value={money(p.subtotal)} />
                <MoneyRow
                  label="Shipping"
                  value={p.shippingNeedsAddress && !address ? "Enter your address" : p.shipping > 0 ? money(p.shipping) : "Included"}
                />
                {p.transitDays && address ? (
                  <p className="text-xs text-brown-400">About {p.transitDays} business days in transit once it ships.</p>
                ) : null}
                <MoneyRow label={p.shippingNeedsAddress && !address ? "Total before shipping" : "Total"} value={money(p.total)} strong />
                {method !== "invoice" && <p className="text-xs text-brown-400">Sales tax, if any, is added when you pay.</p>}
                {method === "invoice" && <p className="text-xs text-brown-400">Sales tax, if any, is added to the invoice.</p>}
              </dl>
            )}
          </div>

          {needs === "yes" && (
            <div className="mt-4">
              <Notice tone="warn">
                {method === "company_card" && my.role !== "admin"
                  ? "Your store admin approves this order and pays for it with the company card. We'll email you when it's approved."
                  : "Your store admin approves this order before we make it. We'll email you when they decide."}
              </Notice>
            </div>
          )}
          {needs === "maybe" && (
            <div className="mt-4">
              <Notice tone="plain">Orders over your company's limit go to your store admin for approval first. We'll email you if this one does.</Notice>
            </div>
          )}
          {paused && (
            <div className="mt-4">
              <Notice tone="warn">{storeOffMessage(my.store, "cart")}</Notice>
            </div>
          )}
          {showProblems && !address && <p className="mt-4 text-xs text-rust-500">Check the delivery address.</p>}
          {submit.isError && (
            <p role="alert" className="mt-4 text-sm text-rust-500">
              {apiMessage(submit.error)}
            </p>
          )}
          <button
            type="button"
            onClick={onSubmit}
            disabled={locked || paused || items.length === 0 || !priced || settling || options.length === 0 || inStore.length !== cart.items.length}
            className={`${PRIMARY} mt-4 w-full`}
          >
            {redirecting ? "Taking you to payment…" : submit.isPending ? "Sending…" : action}
          </button>
        </section>
      </div>
    </div>
  );
}

function Field({ label, error, wide = false, children }: { label: string; error?: string; wide?: boolean; children: ReactElement<{ id?: string; "aria-describedby"?: string }> }) {
  const id = useId();
  return (
    <div className={`flex min-w-0 flex-col gap-1 text-xs text-brown-400 ${wide ? "sm:col-span-2" : ""}`}>
      <label htmlFor={id}>{label}</label>
      {cloneElement(children, { id, ...(error ? { "aria-describedby": `${id}-error` } : {}) })}
      {error && (
        <span id={`${id}-error`} className="text-rust-500">
          {error}
        </span>
      )}
    </div>
  );
}
